import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import { GerUserParams } from "../models/User/GetUserParams";
import { User, UserList } from "../models/User/User";
import { UserActivityLog } from "../models/User/UserActivityLog";
import {
  UserAlertSettings,
  UserAlertSettingsInput,
} from "../models/User/UserAlertSettings";
import { postAPI, getAPI, putAPI, patchAPI, deleteAPI } from "./Networking";
import { PermissionProject } from "../models/Employees/PermissionProject";

export default class UserAPI {
  getUsers = async (
    filter: GerUserParams
  ): Promise<PaginatedAPIResponse<UserList>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filter.page_no) {
        queryParams.append("page_no", filter.page_no.toString());
      }
      if (filter.limit) queryParams.append("limit", filter.limit.toString());

      if (filter.search_text) {
        queryParams.append("search_text", filter.search_text);
      }

      if (filter.customer_id) {
        queryParams.append("customer_id", filter.customer_id.toString());
      }

      if (filter.sort_by) queryParams.append("sort_by", filter.sort_by);
      if (filter.sort_type)
        queryParams.append("sort_type", filter.sort_type.toString());
      if (filter.active_status) {
        const activeQueryValue = {
          active: "true",
          inactive: "false",
          all: "all",
        }[filter.active_status];
        queryParams.append("active", activeQueryValue);
      }
      const response = await getAPI(`/v5/users?${queryParams.toString()}`);

      return response.result as PaginatedAPIResponse<UserList>;
    } catch (error) {
      throw error;
    }
  };

  getGoogleAuthUrl = async (): Promise<string> => {
    try {
      const response = await getAPI(
        `/v5/admin/google/authorizationUrl`
      );
      const result = response.result;
      return (
        result?.authorizationUrl ||
        result?.authorization_url ||
        result?.url ||
        result
      );
    } catch (error) {
      throw error;
    }
  };

  googleAuthCallback = async (code: string): Promise<any> => {
    try {
      const response = await getAPI(
        `/v5/admin/google/auth/callback?code=${code}`
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  checkLogin = async (): Promise<boolean> => {
    try {
      await getAPI("/admin/isLoggedIn");
      return true;
    } catch (error) {
      return false;
    }
  };

  getUserDetails = async (
    project: PermissionProject = "swageazy",
  ): Promise<User> => {
    try {
      const response = await getAPI(
        `/v5/admins/admin/details?project=${project}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  resetPassword = async (email: string) => {
    try {
      const response = await postAPI("/admin/forgetPassword", {
        email,
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  sendCustomerPasswordRecoveryLink = async (
    userId: number,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        "/admin/customer/sendPasswordRecoveryLink",
        { userId },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };


  setNewPassword = async (
    recoveryToken: string,
    otp: string,
    password: string,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI("/admin/setNewPassword", {
        recoveryToken,
        otp,
        password,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  getUserDetailsById = async (userId: number) => {
    try {
      const response = await getAPI(`/v5/users/${userId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  createUser = async (
    userDetails: UserList
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/users`, userDetails);
      return response;
    } catch (error) {
      throw error;
    }
  };

  deleteUser = async (userId: number): Promise<APIResponse> => {
    try {
      const response = await deleteAPI(
        `/v5/users/${userId}`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  activateUser = async (userId: number): Promise<APIResponse> => {
    try {
      const response = await patchAPI(
        `/v1/user/${userId}/activate`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateUserDetails = async (
    userDetails: UserList
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/users`, userDetails);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getIds = async (searchText: string): Promise<number[]> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("search_text", searchText);
      const response = await getAPI(
        `/v5/users?${queryParams.toString()}`
      );
      return response.result.list;
    } catch (error) {
      throw error;
    }
  };

  /** Which client alerts the user receives, in-app and by email (`GET /v1/user/:id/alert-settings`). */
  getUserAlertSettings = async (userId: number): Promise<UserAlertSettings> => {
    const response = await getAPI(`/v1/user/${userId}/alert-settings`);
    return response.result as UserAlertSettings;
  };

  /** Saves in-app / email for one or more client alert types (`PUT /v1/user/:id/alert-settings`). */
  setUserAlertSettings = async (
    userId: number,
    input: UserAlertSettingsInput,
  ): Promise<UserAlertSettings> => {
    const response = await putAPI(`/v1/user/${userId}/alert-settings`, input);
    return response.result as UserAlertSettings;
  };

  getUserActivityLogs = async (
    userId: number,
  ): Promise<UserActivityLog[]> => {
    try {
      const response = await getAPI(`/v5/users/${userId}/activity-logs`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
